/**
 * Issue Enums
 *
 * Typed constants for issue entity types and categories.
 * Use these instead of raw string literals to get compile-time safety.
 *
 * Usage:
 * ```typescript
 * import { ISSUE_ENTITY_TYPE, ISSUE_CATEGORY } from "@nexus/types";
 *
 * void this.issueTracker.report({
 *   entityType: ISSUE_ENTITY_TYPE.SESSION,
 *   // ...
 * });
 * ```
 */

import type { DbEnum } from "../../../generated/prisma-enums";

export const ISSUE_ENTITY_TYPE = {
  SESSION: "session",
  DEPLOYMENT: "deployment",
  PHONE_NUMBER: "phone_number",
  CONNECTION: "connection",
  ORGANIZATION: "organization",
  /** A Vibe tenant cluster. entityId carries the cluster's organizationId (its primary key). */
  VIBE_CLUSTER: "vibe_cluster",
  /**
   * One Vibe app. entityId carries the VibeApp's id.
   *
   * Deliberately finer than VIBE_CLUSTER: a backup alarm names the app whose
   * data is at risk, and dedupe is per-app so one broken app cannot mask a
   * second one breaking on the same tenant — which is exactly what a
   * cluster-scoped code would do.
   */
  VIBE_APP: "vibe_app",
  /**
   * One Vibe git project (code store). entityId carries the VibeGitProject's id.
   *
   * Deliberately finer than VIBE_CLUSTER, same reasoning as VIBE_APP: a
   * project whose repository has vanished from Forgejo names that project,
   * and dedupe is per-project so one broken project cannot mask a second one
   * on the same tenant.
   */
  VIBE_GIT_PROJECT: "vibe_git_project",
  /**
   * The Vibe fleet as a whole — a PLATFORM-scope issue the tenant alert engine's
   * fleet detectors raise (the engine's own coverage census, the edge prober, a
   * region nobody observed). entityId carries `fleet`: each such code is one
   * problem for the whole platform, so one row.
   */
  VIBE_FLEET: "vibe_fleet",
  /**
   * One tenant alert detector that could not read its signal. entityId carries
   * `<detector>:<organizationId>`, so the row is one detector going blind on one
   * tenant, and a sweep that retracts by entity across tenants never lets one
   * tenant's blind detector hold another's open.
   */
  VIBE_ALERT_DETECTOR: "vibe_alert_detector",
  /**
   * One live database a schema-drift check compares against its migrations. entityId carries the
   * check's own name for it — `prod`, `echo-prod`, `staging`, … — so the two Prisma packages of
   * one environment are two rows: they drift independently and are repaired independently.
   */
  DATABASE: "database",
  /**
   * One deployment environment as a whole (`prod`, `staging`, `dev`). entityId carries the
   * environment's slug. For a problem with no finer owner than the environment — a secret store
   * that is not reaching that environment's pods.
   */
  DEPLOYMENT_ENVIRONMENT: "deployment_environment",
  /**
   * One AWS account, by its logical name in `deployment/aws-accounts.json` (`vibe`), never by its
   * numeric id: the name is what a reader recognises and what survives a move between accounts.
   */
  AWS_ACCOUNT: "aws_account",
  /**
   * One GitHub Actions workflow on one branch, as `<branch>/<workflow file>` —
   * `staging/post-merge-checks.yml`. The branch is part of the id because the same file judges
   * each branch it runs on independently, and a green run on one must never resolve a red one on
   * another.
   */
  CI_WORKFLOW: "ci_workflow",
  /** One monitored external API. entityId carries the monitor's API name — `openai`, `anthropic`, `groq`, `google` or `pipedream`. */
  EXTERNAL_API: "external_api",
  /** One model provider's shared account. entityId carries the `ModelProvider` enum value. */
  MODEL_PROVIDER: "model_provider",
  /** One code-interpreter surface's sandbox fleet. entityId carries the `DaytonaTraceSource` value. */
  SANDBOX_SURFACE: "sandbox_surface",
  /** One capability of the deployment. entityId carries the capability's registered name. */
  CAPABILITY: "capability"
} as const;

export type IssueEntityType = (typeof ISSUE_ENTITY_TYPE)[keyof typeof ISSUE_ENTITY_TYPE];

export const ISSUE_CATEGORY = {
  PROVIDER: "provider",
  BILLING: "billing",
  WEBHOOK: "webhook",
  /** Internal telemetry health — tracing, cost attribution, metrics pipelines. */
  OBSERVABILITY: "observability",
  /** This deployment's own configuration — a variable absent or unusable at process start. */
  CONFIGURATION: "configuration"
} as const;

export type IssueCategory = (typeof ISSUE_CATEGORY)[keyof typeof ISSUE_CATEGORY];

/**
 * Fault attribution — answers the question users actually ask: "is Nexus broken,
 * or does something on my side / my provider's side need attention?"
 *
 * `category` says which subsystem; `fault` says *who must act*. This drives the
 * tone and color of the user-facing callout and whether a CTA is shown.
 */
export const ISSUE_FAULT = {
  /** The external provider has an outage or rejected us. Not Nexus, not the user. */
  PROVIDER: "provider",
  /** The customer must act on the provider side (reconnect, register, verify, approve). */
  USER_ACTION: "user_action",
  /** Setup inside Nexus is incomplete (no agent, no connection, no phone number). */
  CONFIG: "config",
  /** Genuinely a Nexus-side problem. Owned honestly rather than blamed elsewhere. */
  NEXUS: "nexus"
} as const;

export type IssueFault = (typeof ISSUE_FAULT)[keyof typeof ISSUE_FAULT];

/**
 * Operational severity, shared by the registry, the `Issue.severity` column and user-facing view
 * models. Derived from the database enum, so the registry cannot name a severity the column
 * cannot store.
 */
export type IssueSeverity = (typeof DbEnum.ISSUE_SEVERITY_VALUES)[number];

/**
 * The runtime that emitted an issue — `BACKEND` for every report made in-process, `WORKFLOW` for
 * one a GitHub Actions workflow sent through the issue-ingest route. Derived from the database
 * enum like `IssueSeverity`.
 */
export type IssueSource = (typeof DbEnum.ISSUE_SOURCE_VALUES)[number];
